import { post, isSuccess, extractMessage } from '../../services/api';

function createDefaultBookingData() {
  return {
    layout: null,
    seats: [],
    tariffs_hourly: [],
    tariffs_package: [],
    package_templates: [],
    slot_step_mins: 1,
  };
}

function normalizeBookingData(data = {}) {
  return {
    layout: data.layout || null,
    seats: Array.isArray(data.seats) ? data.seats : [],
    tariffs_hourly: Array.isArray(data.tariffs_hourly) ? data.tariffs_hourly : [],
    tariffs_package: Array.isArray(data.tariffs_package) ? data.tariffs_package : [],
    package_templates: Array.isArray(data.package_templates) ? data.package_templates : [],
    slot_step_mins: Number(data.slot_step_mins || 1),
  };
}

export default {
  namespaced: true,

  state: () => ({
    initLoading: false,
    availabilityLoading: false,
    submitting: false,
    pageError: '',
    submitError: '',
    bookingData: createDefaultBookingData(),
    busySeatIds: [],
    confirmedEndTime: '',
  }),

  getters: {
    initLoading: (state) => state.initLoading,
    availabilityLoading: (state) => state.availabilityLoading,
    submitting: (state) => state.submitting,
    pageError: (state) => state.pageError,
    submitError: (state) => state.submitError,
    bookingData: (state) => state.bookingData,
    layout: (state) => state.bookingData.layout || null,
    seats: (state) => state.bookingData.seats,
    hourlyTariffs: (state) => state.bookingData.tariffs_hourly,
    packageTariffsRaw: (state) => state.bookingData.tariffs_package,
    packageTemplatesRaw: (state) => state.bookingData.package_templates,
    slotStepMins: (state) => Math.max(1, Number(state.bookingData.slot_step_mins || 30)),
    busySeatIds: (state) => state.busySeatIds,
    confirmedEndTime: (state) => state.confirmedEndTime,
  },

  mutations: {
    SET_INIT_LOADING(state, value) {
      state.initLoading = value;
    },
    SET_AVAILABILITY_LOADING(state, value) {
      state.availabilityLoading = value;
    },
    SET_SUBMITTING(state, value) {
      state.submitting = value;
    },
    SET_PAGE_ERROR(state, value) {
      state.pageError = value;
    },
    SET_SUBMIT_ERROR(state, value) {
      state.submitError = value;
    },
    SET_BOOKING_DATA(state, data) {
      state.bookingData = normalizeBookingData(data);
    },
    SET_BUSY_SEAT_IDS(state, items) {
      state.busySeatIds = Array.isArray(items) ? items.map(Number) : [];
    },
    SET_CONFIRMED_END_TIME(state, value) {
      state.confirmedEndTime = String(value || '');
    },
    RESET_AVAILABILITY(state) {
      state.busySeatIds = [];
      state.confirmedEndTime = '';
      state.submitError = '';
    },
    RESET_STATE(state) {
      state.initLoading = false;
      state.availabilityLoading = false;
      state.submitting = false;
      state.pageError = '';
      state.submitError = '';
      state.bookingData = createDefaultBookingData();
      state.busySeatIds = [];
      state.confirmedEndTime = '';
    },
  },

  actions: {
    resetState({ commit }) {
      commit('RESET_STATE');
    },

    resetAvailability({ commit }) {
      commit('RESET_AVAILABILITY');
    },

    clearSubmitError({ commit }) {
      commit('SET_SUBMIT_ERROR', '');
    },

    async initBooking({ commit }, clubId) {
      commit('SET_INIT_LOADING', true);
      commit('SET_PAGE_ERROR', '');
      commit('SET_SUBMIT_ERROR', '');

      try {
        const result = await post('/booking/init', {
          club_id: Number(clubId),
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось загрузить параметры бронирования');
          commit('SET_PAGE_ERROR', message);
          commit('SET_BOOKING_DATA', createDefaultBookingData());
          return { ok: false, message };
        }

        commit('SET_BOOKING_DATA', result.data || {});
        return { ok: true, data: result.data || {} };
      } catch (error) {
        const message = 'Не удалось загрузить параметры бронирования';
        commit('SET_PAGE_ERROR', message);
        commit('SET_BOOKING_DATA', createDefaultBookingData());
        return { ok: false, message };
      } finally {
        commit('SET_INIT_LOADING', false);
      }
    },

    async checkAvailability({ commit }, payload) {
      commit('SET_AVAILABILITY_LOADING', true);
      commit('SET_SUBMIT_ERROR', '');

      try {
        const result = await post('/booking/availability', payload);

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось проверить занятость мест');
          commit('SET_SUBMIT_ERROR', message);
          return { ok: false, message };
        }

        const data = result.data || {};
        commit('SET_BUSY_SEAT_IDS', data.busy_seat_ids || []);
        commit('SET_CONFIRMED_END_TIME', data.end_time || '');
        return { ok: true, data };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_SUBMIT_ERROR', message);
        return { ok: false, message };
      } finally {
        commit('SET_AVAILABILITY_LOADING', false);
      }
    },

    async createBooking({ commit, dispatch, rootGetters }, payload) {
      commit('SET_SUBMITTING', true);
      commit('SET_SUBMIT_ERROR', '');

      try {
        const user = rootGetters['auth/user'];
        if (!user?.hash) {
          const message = 'Пользователь не авторизован';
          commit('SET_SUBMIT_ERROR', message);
          return { ok: false, message };
        }

        const result = await post('/booking/create', {
          hash: user.hash,
          ...payload,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось создать бронь');
          commit('SET_SUBMIT_ERROR', message);
          return { ok: false, message };
        }

        const order = result?.data?.order || result?.data || null;

        // Важно: создание брони не должно падать, если модуль orders ещё не обновлён
        // или экран был открыт до полной инициализации store. Бронь уже создана на сервере,
        // поэтому синхронизацию списка делаем безопасно и не блокируем подтверждение.
        if (order?.order_number) {
          try {
            commit('orders/UPSERT_ORDER', order, { root: true });
          } catch (syncError) {}

          try {
            Promise.resolve(dispatch('orders/loadOrders', { reset: true }, { root: true })).catch(() => {});
          } catch (syncError) {}
        }

        return { ok: true, order };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_SUBMIT_ERROR', message);
        return { ok: false, message };
      } finally {
        commit('SET_SUBMITTING', false);
      }
    },
  },
};
